import { alpha } from '@mui/material/styles';
import { darkSurfaces } from './darkSurfaces';

export const tableHeaderTypography = {
  fontSize: '10px',
  textTransform: 'none',
  letterSpacing: 'normal',
};

/**
 * Glassmorphism Design System
 * A comprehensive collection of glassmorphic UI patterns and utilities
 * for creating modern, frosted glass effects throughout the application.
 */

// ============================================================================
// CORE GLASSMORPHISM PATTERNS
// ============================================================================

// ============================================================================
// DESIGN TOKENS - Premium Linear/Stripe-inspired values
// ============================================================================

export const designTokens = {
  // Typography
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    sizes: {
      xs: '11px',
      sm: '12px',
      base: '13px',
      md: '14px',
      lg: '16px',
      xl: '18px',
      '2xl': '20px',
      '3xl': '24px',
    },
    weights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.625,
    },
    letterSpacing: {
      tight: '-0.01em',
      normal: '0',
      wide: '0.02em',
      wider: '0.04em',
      caps: '0.06em',
    },
  },

  // Spacing (8pt grid system)
  spacing: {
    0: '0',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    16: '64px',
  },

  // Border radius (unified system - Linear/Stripe style)
  radius: {
    card: '10px',      // Cards, containers, panels
    input: '7px',      // Inputs, buttons, controls
    chip: '9999px',    // Pills, tags, badges
    xs: '4px',
    sm: '6px',
    md: '7px',
    lg: '10px',
    xl: '12px',
    '2xl': '14px',
    '3xl': '16px',
  },

  // 3-Level Surface System (A = background, B = containers, C = cards)
  surfaces: {
    light: {
      // Level A: Background
      bgA: '#F7F8FA',
      blurA: 'blur(0px)',
      opacityA: 1,
      // Level B: Containers (sidebar, main panels)
      bgB: 'rgba(255, 255, 255, 0.84)',
      blurB: 'blur(24px)',
      opacityB: 0.65,
      // Level C: Cards, table rows, inner elements
      bgC: 'rgba(255, 255, 255, 0.62)',
      blurC: 'blur(12px)',
      opacityC: 0.5,
    },
    dark: {
      // Level A: Background
      bgA: darkSurfaces.canvas,
      blurA: 'blur(0px)',
      opacityA: 1,
      // Level B: Containers
      bgB: darkSurfaces.glass,
      blurB: 'blur(24px)',
      opacityB: 0.7,
      // Level C: Cards, inner elements
      bgC: darkSurfaces.panel,
      blurC: 'blur(12px)',
      opacityC: 0.55,
    },
  },

  // Borders (hairline system - very subtle)
  borders: {
    light: {
      hairline: 'rgba(0, 0, 0, 0.04)',
      subtle: 'rgba(0, 0, 0, 0.05)',
      default: 'rgba(0, 0, 0, 0.08)',
      strong: 'rgba(0, 0, 0, 0.12)',
    },
    dark: {
      hairline: 'rgba(255, 255, 255, 0.03)',
      subtle: 'rgba(255, 255, 255, 0.04)',
      default: 'rgba(255, 255, 255, 0.06)',
      strong: 'rgba(255, 255, 255, 0.1)',
    },
  },

  // Shadows (2-level system: low + medium, very diffuse)
  shadows: {
    none: 'none',
    low: '0 1px 2px rgba(15, 23, 42, 0.03), 0 1px 3px rgba(15, 23, 42, 0.018)',
    med: '0 1px 3px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.025)',
    // Dark mode
    lowDark: '0 1px 2px rgba(0, 0, 0, 0.14), 0 1px 3px rgba(0, 0, 0, 0.08)',
    medDark: '0 1px 3px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.12)',
  },

  // Text colors (high contrast for readability)
  text: {
    light: {
      primary: 'rgba(0, 0, 0, 0.9)',      // Almost black
      secondary: 'rgba(0, 0, 0, 0.6)',    // Clearly readable
      muted: 'rgba(0, 0, 0, 0.4)',        // Subtle but visible
      disabled: 'rgba(0, 0, 0, 0.25)',
    },
    dark: {
      primary: 'rgba(255, 255, 255, 0.95)',
      secondary: 'rgba(255, 255, 255, 0.7)',
      muted: 'rgba(255, 255, 255, 0.45)',
      disabled: 'rgba(255, 255, 255, 0.25)',
    },
  },

  // Accent color system (mainColor variants)
  accent: {
    // These are multipliers/opacities to apply to mainColor
    bg10: 0.1,   // Very subtle background
    bg20: 0.2,   // Light background
    text: 0.9,   // Text on light bg
    solid: 1,    // Full color
  },

  // Transitions
  transitions: {
    fast: '100ms cubic-bezier(0.4, 0, 0.2, 1)',
    base: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    smooth: '350ms cubic-bezier(0.16, 1, 0.3, 1)',
  },

  // Focus states
  focus: {
    light: '0 0 0 2px rgba(0, 0, 0, 0.05)',
    dark: '0 0 0 2px rgba(255, 255, 255, 0.08)',
    accent: (color) => `0 0 0 2px ${color}20`,
  },
};

export const glassmorphismPatterns = {
  // Base glassmorphic surface (Level B - containers)
  base: {
    background: (theme) => theme.palette.mode === 'dark'
      ? designTokens.surfaces.dark.bgB
      : designTokens.surfaces.light.bgB,
    backdropFilter: 'blur(24px) saturate(150%)',
    WebkitBackdropFilter: 'blur(24px) saturate(150%)',
    border: (theme) => `1px solid ${theme.palette.mode === 'dark'
      ? designTokens.borders.dark.subtle
      : designTokens.borders.light.subtle}`,
    borderRadius: designTokens.radius.card,
    boxShadow: (theme) => theme.palette.mode === 'dark'
      ? designTokens.shadows.lowDark
      : designTokens.shadows.low,
  },

  // Elevated glassmorphic surface (for cards, modals - Level C)
  elevated: {
    background: (theme) => theme.palette.mode === 'dark'
      ? designTokens.surfaces.dark.bgC
      : designTokens.surfaces.light.bgC,
    backdropFilter: 'blur(12px) saturate(140%)',
    WebkitBackdropFilter: 'blur(12px) saturate(140%)',
    border: (theme) => `1px solid ${theme.palette.mode === 'dark'
      ? designTokens.borders.dark.hairline
      : designTokens.borders.light.hairline}`,
    borderRadius: designTokens.radius.lg,
    boxShadow: (theme) => theme.palette.mode === 'dark'
      ? designTokens.shadows.medDark
      : designTokens.shadows.med,
  },

  // Subtle glassmorphic surface (for overlays, backgrounds)
  subtle: {
    background: (theme) => theme.palette.mode === 'dark'
      ? darkSurfaces.glass
      : alpha(theme.palette.background.paper, 0.4),
    backdropFilter: 'blur(8px) saturate(130%)',
    WebkitBackdropFilter: 'blur(8px) saturate(130%)',
    border: (theme) => `1px solid ${theme.palette.mode === 'dark'
      ? designTokens.borders.dark.hairline
      : designTokens.borders.light.hairline}`,
    borderRadius: designTokens.radius.lg,
    boxShadow: (theme) => theme.palette.mode === 'dark'
      ? designTokens.shadows.lowDark
      : designTokens.shadows.low,
  },

  // Interactive glassmorphic surface (for buttons, chips)
  interactive: {
    background: (theme) => theme.palette.mode === 'dark'
      ? darkSurfaces.inset
      : alpha(theme.palette.background.paper, 0.5),
    backdropFilter: 'blur(8px) saturate(130%)',
    WebkitBackdropFilter: 'blur(8px) saturate(130%)',
    border: (theme) => `1px solid ${theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.12)'}`,
    borderRadius: designTokens.radius.input,
    boxShadow: (theme) => theme.palette.mode === 'dark'
      ? designTokens.shadows.lowDark
      : designTokens.shadows.low,
  },
};

// ============================================================================
// BACKGROUND PATTERNS
// ============================================================================

export const backgroundPatterns = {
  // Gradient background with subtle color infusion
  gradient: (theme) => ({
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.05
    )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 20% 80%, ${alpha(
        theme.palette.primary.main,
        0.1
      )} 0%, transparent 50%),
                  radial-gradient(circle at 80% 20%, ${alpha(
        theme.palette.secondary.main,
        0.1
      )} 0%, transparent 50%)`,
      pointerEvents: 'none',
    },
  }),

  // Color-specific gradient background
  colorGradient: (color) => ({
    background: `linear-gradient(135deg, ${alpha(color, 0.1)}, ${alpha(
      color,
      0.05
    )})`,
    backdropFilter: 'blur(10px)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 30% 30%, ${alpha(
        color,
        0.2
      )} 0%, transparent 70%)`,
      pointerEvents: 'none',
    },
  }),

  // Animated gradient background
  animatedGradient: (theme) => ({
    background: `linear-gradient(-45deg, ${alpha(
      theme.palette.primary.main,
      0.1
    )}, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(
      theme.palette.primary.main,
      0.05
    )}, ${alpha(theme.palette.secondary.main, 0.05)})`,
    backgroundSize: '400% 400%',
    animation: 'gradientShift 15s ease infinite',
    '@keyframes gradientShift': {
      '0%': { backgroundPosition: '0% 50%' },
      '50%': { backgroundPosition: '100% 50%' },
      '100%': { backgroundPosition: '0% 50%' },
    },
  }),
};

// ============================================================================
// COMPONENT PATTERNS
// ============================================================================

export const componentPatterns = {
  card: (theme, color = null) => ({
    ...glassmorphismPatterns.elevated,
    background: (theme) => theme.palette.mode === 'dark'
      ? darkSurfaces.glass
      : alpha(theme.palette.background.paper, 0.82),
    backdropFilter: 'blur(24px) saturate(140%)',
    overflow: 'hidden',
    position: 'relative',
    transition: 'box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.25s ease',
    ...(color && {
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2.5px',
        zIndex: 1,
        background: `linear-gradient(90deg, ${alpha(color, 0.6)}, ${alpha(color, 0.15)})`,
        opacity: 0.7,
      },
    }),
    '&:hover': {
      boxShadow: (theme) =>
        `0 8px 24px ${alpha(
          theme.palette.common.black,
          0.06
        )}, 0 2px 8px ${alpha(color || theme.palette.primary.main, 0.08)}`,
      borderColor: (theme) => alpha(theme.palette.divider, 0.05),
    },
  }),

  // Shared block/card header: clear hierarchy without adding visual weight.
  blockHeader: (theme) => ({
    minHeight: '36px',
    padding: '4px 12px',
    gap: 1,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.008)'
        : 'rgba(0, 0, 0, 0.008)',
    borderBottom: `1px solid ${
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.035)'
        : 'rgba(0, 0, 0, 0.035)'
    }`,
    '& .block-header-title': {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: 1.3,
      color: theme.palette.text.primary,
      letterSpacing: '-0.01em',
    },
    '& .block-header-icon': {
      width: '18px',
      height: '18px',
      fontSize: '15px',
      flexShrink: 0,
    },
    '& .block-header-emoji': {
      width: '16px',
      height: '16px',
      fontSize: '13px',
      flexShrink: 0,
      lineHeight: 1,
    },
    '& .block-header-actions': {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      '& .MuiIconButton-root': {
        width: 26,
        height: 26,
        padding: '3px',
        opacity: 0.6,
        transition: 'opacity 150ms ease, color 150ms ease',
        '&:hover': {
          opacity: 1,
        },
        '& .MuiSvgIcon-root': {
          fontSize: '16px',
        },
      },
      '& .block-action-button': {
        opacity: 1,
      },
    },
  }),

  // Glassmorphic button
  button: (theme, color = null) => ({
    ...glassmorphismPatterns.interactive,
    color: theme.palette.text.primary,
    background: color
      ? `linear-gradient(135deg, ${alpha(color, 0.15)}, ${alpha(color, 0.05)})`
      : alpha(theme.palette.background.paper, 0.6),
    backdropFilter: 'blur(10px)',
    border: (theme) =>
      `1px solid ${color ? alpha(color, 0.3) : alpha(theme.palette.divider, 0.1)
      }`,
    borderRadius: designTokens.radius.input,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      color: theme.palette.text.primary,
      background: color
        ? `linear-gradient(135deg, ${alpha(color, 0.2)}, ${alpha(color, 0.1)})`
        : `linear-gradient(135deg, ${alpha(
          theme.palette.action.hover,
          0.1
        )}, ${alpha(theme.palette.action.hover, 0.05)})`,
      transform: 'translateY(-1px)',
      borderColor: color
        ? alpha(color, 0.4)
        : alpha(theme.palette.divider, 0.2),
      boxShadow: (theme) =>
        `0 8px 20px ${alpha(color || theme.palette.primary.main, 0.2)}`,
    },
  }),

  // Premium chip/tag (Linear/Stripe style)
  chip: (theme, color) => ({
    background: alpha(color, 0.08),
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: `1px solid ${alpha(color, 0.12)}`,
    color: theme.palette.mode === 'dark'
      ? alpha(color, 0.9)
      : color,
    fontWeight: 500,
    fontSize: '11px',
    letterSpacing: '0.01em',
    height: '22px',
    borderRadius: '9999px',
    transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    '& .MuiChip-label': {
      padding: '0 10px',
    },
    '&:hover': {
      background: alpha(color, 0.12),
      borderColor: alpha(color, 0.18),
    },
  }),

  // Status chip (for Actif, Inactif, etc.)
  statusChip: (theme, status) => {
    const statusColors = {
      active: '#22C55E',
      inactive: '#94A3B8',
      pending: '#F59E0B',
      error: '#EF4444',
    };
    const color = statusColors[status] || statusColors.inactive;
    return {
      background: alpha(color, 0.1),
      border: `1px solid ${alpha(color, 0.15)}`,
      color: theme.palette.mode === 'dark' ? alpha(color, 0.95) : color,
      fontWeight: 500,
      fontSize: '11px',
      height: '22px',
      borderRadius: '9999px',
      padding: '0 10px',
    };
  },

  // Glassmorphic sidebar
  sidebar: (theme) => ({
    ...glassmorphismPatterns.base,
    background: (theme) => alpha(theme.palette.background.paper, 0.7),
    backdropFilter: 'blur(20px)',
    borderRadius: '12px',
    boxShadow: (theme) =>
      `0 4px 16px ${alpha(theme.palette.common.black, 0.1)}`,
  }),

  // Glassmorphic icon container
  iconContainer: (color) => ({
    background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.8)})`,
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 4px 12px ${alpha(color, 0.2)}`,
    position: 'relative',
    zIndex: 1,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: `0 6px 16px ${alpha(color, 0.25)}`,
    },
  }),

  // Premium table patterns (Linear/Stripe style)
  table: {
    container: (theme) => ({
      background: theme.palette.mode === 'dark'
        ? 'rgba(28, 28, 34, 0.5)'
        : 'rgba(255, 255, 255, 0.45)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderRadius: '9px',
      border: `1px solid ${theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.04)'
        : 'rgba(0, 0, 0, 0.05)'}`,
      overflow: 'hidden',
    }),
    header: (theme) => ({
      background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)',
      borderBottom: `1px solid ${theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.06)'}`,
      '& th': {
        ...tableHeaderTypography,
        fontWeight: 500,
        color: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.5)'
          : 'rgba(0, 0, 0, 0.5)',
        padding: '10px 12px',
      },
    }),
    row: (theme) => ({
      borderBottom: `1px solid ${theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.04)'
        : 'rgba(0, 0, 0, 0.04)'}`,
      transition: 'background 100ms ease',
      '&:last-child': {
        borderBottom: 'none',
      },
      '&:hover': {
        background: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.03)'
          : 'rgba(0, 0, 0, 0.015)',
      },
      '& td': {
        padding: '12px',
        fontSize: '13px',
        verticalAlign: 'middle',
      },
    }),
    cell: {
      primary: (theme) => ({
        fontWeight: 500,
        color: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.95)'
          : 'rgba(0, 0, 0, 0.9)',
      }),
      secondary: (theme) => ({
        fontWeight: 400,
        color: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.6)'
          : 'rgba(0, 0, 0, 0.55)',
        fontSize: '12px',
      }),
      muted: (theme) => ({
        fontWeight: 400,
        color: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.35)'
          : 'rgba(0, 0, 0, 0.35)',
        fontSize: '12px',
      }),
    },
  },

  // Segmented control (tabs like Détails/Qualité/Formulaire)
  segmentedControl: {
    container: (theme) => ({
      display: 'inline-flex',
      background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)',
      borderRadius: '10px',
      padding: '3px',
      gap: '2px',
    }),
    segment: (theme, isActive) => ({
      padding: '6px 14px',
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: isActive ? 500 : 400,
      color: isActive
        ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.9)')
        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
      background: isActive
        ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.8)')
        : 'transparent',
      boxShadow: isActive
        ? (theme.palette.mode === 'dark'
          ? '0 1px 2px rgba(0, 0, 0, 0.2)'
          : '0 1px 2px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)')
        : 'none',
      transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      '&:hover': {
        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
      },
    }),
  },

  // Toolbar button (unified action buttons)
  toolbarButton: (theme) => ({
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.06)'}`,
    background: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(255, 255, 255, 0.6)',
    transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)',
      borderColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.1)',
    },
    '& svg': {
      fontSize: '16px',
      strokeWidth: 1.5,
    },
  }),
};

// ============================================================================
// TYPOGRAPHY PATTERNS
// ============================================================================

export const typographyPatterns = {
  gradientText: (theme, color1 = null, color2 = null) => ({
    background: `linear-gradient(135deg, ${color1 || theme.palette.text.primary
      }, ${color2 || alpha(theme.palette.text.primary, 0.8)})`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 600,
  }),

  // Glassmorphic text with shadow
  glassText: (theme) => ({
    color: theme.palette.text.primary,
    textShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.1)}`,
    fontWeight: 500,
  }),

  // Icon styling
  icon: {
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
};

// ============================================================================
// ANIMATION PATTERNS
// ============================================================================

export const animationPatterns = {
  // Smooth transitions
  smooth: {
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Card hover animation
  cardHover: {
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },

  // Button hover animation
  buttonHover: {
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },

  // Icon hover animation
  iconHover: {
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'scale(1.1) rotate(5deg)',
    },
  },

  // Slide animation
  slideIn: {
    animation: 'slideIn 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    '@keyframes slideIn': {
      '0%': { transform: 'translateX(-20px)', opacity: 0 },
      '100%': { transform: 'translateX(0)', opacity: 1 },
    },
  },

  // Fade animation
  fadeIn: {
    animation: 'fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    '@keyframes fadeIn': {
      '0%': { opacity: 0, transform: 'translateY(20px)' },
      '100%': { opacity: 1, transform: 'translateY(0)' },
    },
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const glassmorphismUtils = {
  // Create glassmorphic styles with theme
  createGlassmorphicStyles: (theme, pattern = 'base', customProps = {}) => {
    const basePattern = glassmorphismPatterns[pattern];
    return {
      ...basePattern,
      ...(typeof basePattern.background === 'function' && {
        background: basePattern.background(theme),
      }),
      ...(typeof basePattern.border === 'function' && {
        border: basePattern.border(theme),
      }),
      ...(typeof basePattern.boxShadow === 'function' && {
        boxShadow: basePattern.boxShadow(theme),
      }),
      ...customProps,
    };
  },

  // Create color-specific glassmorphic styles
  createColorGlassmorphicStyles: (
    theme,
    color,
    pattern = 'base',
    customProps = {}
  ) => {
    const baseStyles = glassmorphismUtils.createGlassmorphicStyles(
      theme,
      pattern,
      customProps
    );
    return {
      ...baseStyles,
      background: `linear-gradient(135deg, ${alpha(color, 0.15)}, ${alpha(
        color,
        0.05
      )})`,
      border: `1px solid ${alpha(color, 0.2)}`,
      '&:hover': {
        background: `linear-gradient(135deg, ${alpha(color, 0.2)}, ${alpha(
          color,
          0.1
        )})`,
        borderColor: alpha(color, 0.3),
      },
    };
  },

  // Create responsive glassmorphic styles
  createResponsiveGlassmorphicStyles: (theme, breakpoint = 'md') => ({
    ...glassmorphismPatterns.base,
    [theme.breakpoints.down(breakpoint)]: {
      ...glassmorphismPatterns.subtle,
    },
  }),
};

// ============================================================================
// PRESET CONFIGURATIONS
// ============================================================================

export const glassmorphismPresets = {
  // Email templates specific preset
  emailTemplates: {
    container: (theme) => ({
      gap: 3,
      p: 3,
      height: '100%',
      minHeight: '100vh',
      position: 'relative',
    }),
    sidebar: (theme) => componentPatterns.sidebar(theme),
    card: (theme, color) => componentPatterns.card(theme, color),
    categoryButton: (theme, color, isActive) => ({
      cursor: 'pointer',
      borderRadius: '16px',
      p: 1.5,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      background: isActive
        ? `linear-gradient(135deg, ${alpha(color, 0.15)}, ${alpha(
          color,
          0.05
        )})`
        : 'transparent',
      border: `1px solid ${isActive ? alpha(color, 0.3) : alpha(theme.palette.divider, 0.1)
        }`,
      backdropFilter: isActive ? 'blur(10px)' : 'none',
      boxShadow: isActive ? `0 4px 12px ${alpha(color, 0.2)}` : 'none',
      '&:hover': {
        background: isActive
          ? `linear-gradient(135deg, ${alpha(color, 0.2)}, ${alpha(
            color,
            0.1
          )})`
          : `linear-gradient(135deg, ${alpha(
            theme.palette.action.hover,
            0.1
          )}, ${alpha(theme.palette.action.hover, 0.05)})`,
        transform: 'translateX(4px)',
        borderColor: isActive
          ? alpha(color, 0.4)
          : alpha(theme.palette.divider, 0.2),
        boxShadow: isActive
          ? `0 6px 16px ${alpha(color, 0.3)}`
          : `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
      },
    }),
  },

  // Dashboard preset
  dashboard: {
    container: (theme) => ({
      ...backgroundPatterns.animatedGradient(theme),
      p: 2,
      minHeight: '100vh',
    }),
    card: (theme) => componentPatterns.card(theme),
    widget: (theme) => ({
      ...glassmorphismPatterns.subtle,
      p: 2,
      borderRadius: '16px',
    }),
  },

  // Modal preset
  modal: {
    backdrop: (theme) => ({
      background: alpha(theme.palette.common.black, 0.3),
      backdropFilter: 'blur(8px)',
    }),
    content: (theme) => ({
      ...glassmorphismPatterns.elevated,
      maxWidth: '90vw',
      maxHeight: '90vh',
      overflow: 'auto',
    }),
  },
};

// ============================================================================
// NOTIFICATION POPPER & CARD PATTERNS (from TopBar)
// ============================================================================

export const notificationPatterns = {
  // Notification popper (container)
  popper: (theme) => ({
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)',
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(24, 24, 32, 0.75)'
        : 'rgba(255, 255, 255, 0.75)',
    border:
      theme.palette.mode === 'dark'
        ? '1.5px solid rgba(255,255,255,0.12)'
        : '1.5px solid rgba(200,200,255,0.18)',
    backdropFilter: 'blur(16px) saturate(180%)',
    WebkitBackdropFilter: 'blur(16px) saturate(180%)',
    borderRadius: '16px',
    minWidth: 340,
    maxHeight: 580,
    overflowY: 'auto',
    transition: 'all 0.3s',
  }),
  // Notification card (individual notification)
  card: (theme, { isViewed = false } = {}) => ({
    mb: 1.5,
    py: 1,
    px: 2,
    borderRadius: '16px',
    background: isViewed
      ? theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.10)'
        : 'rgba(255,255,255,0.30)'
      : theme.palette.mode === 'dark'
        ? 'rgba(120, 180, 255, 0.18)'
        : 'rgba(120, 180, 255, 0.25)',
    border: isViewed
      ? '1px solid rgba(255,255,255,0.10)'
      : '1.5px solid #42a5f5',
    boxShadow: isViewed
      ? '0 2px 8px 0 rgba(31, 38, 135, 0.08)'
      : '0 4px 16px 0 rgba(66, 165, 245, 0.10)',
    fontSize: 12,
    fontWeight: isViewed ? 400 : 600,
    color: isViewed ? theme.palette.text.primary : theme.palette.primary.main,
    transition: 'all 0.2s',
    '&:hover': {
      background:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.secondary.main, 0.1)
          : alpha(theme.palette.secondary.main, 0.1),
      boxShadow: `0 6px 24px 0 ${alpha(theme.palette.secondary.main, 0.1)}`,
    },
  }),
  // Notification 'Load more' button style
  loadMoreButton: (theme) => ({
    mt: 2,
    borderRadius: 12,
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(120, 180, 255, 0.18)'
        : 'rgba(120, 180, 255, 0.22)',
    color: theme.palette.primary.main,
    fontWeight: 600,
    boxShadow: '0 2px 8px 0 rgba(66, 165, 245, 0.10)',
    textTransform: 'none',
    '&:hover': {
      background:
        theme.palette.mode === 'dark'
          ? 'rgba(120, 180, 255, 0.28)'
          : 'rgba(120, 180, 255, 0.32)',
    },
  }),
};

// ============================================================================
// EXPORT ALL PATTERNS
// ============================================================================

export default {
  tokens: designTokens,
  patterns: glassmorphismPatterns,
  backgrounds: backgroundPatterns,
  components: componentPatterns,
  typography: typographyPatterns,
  animations: animationPatterns,
  utils: glassmorphismUtils,
  presets: glassmorphismPresets,
  notification: notificationPatterns,
};
